/**
 * XNGAGE CONFIDENTIAL
 * __________________________
 *
 * Copyright (C) 2020 Xngage - All Rights Reserved
 *
 * All code or information contained herein is, and remains the
 * property of Xngage LLC and its customers.  The intellectual
 * and technical concepts contained are proprietary to Xngage LLC
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Xngage LLC.
 */
import { screen } from "@eisinc/XDL/Helpers";
import { color, printStyles } from "@eisinc/XDL/Utilities";
import { onPrint } from "@eisinc/XDL/Utilities/print";
import { DataTableBodyProps } from "@insite/mobius/DataTable/DataTableBody";
import { DataTableCellProps } from "@insite/mobius/DataTable/DataTableCell";
import { DataTableHeadProps } from "@insite/mobius/DataTable/DataTableHead";
import { DataTableHeaderProps } from "@insite/mobius/DataTable/DataTableHeader";
import { DataTableRowProps } from "@insite/mobius/DataTable/DataTableRow";
import { GridOffset } from "@insite/mobius/GridContainer";
import { GridItemProps } from "@insite/mobius/GridItem";
import { HiddenProps } from "@insite/mobius/Hidden";
import { LinkPresentationProps } from "@insite/mobius/Link";
import { TypographyPresentationProps } from "@insite/mobius/Typography";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { css } from "styled-components";

export interface OrderDetailsStyles {
    tableWarpper: InjectableCss;
    tableWrapperHeader: InjectableCss;
    infoLabel: TypographyPresentationProps;
    tableBodyStyle: DataTableBodyProps;
    tableBodyWrapper?: InjectableCss;
    tableHeadStyle: DataTableHeadProps;
    tableHeaderStyle: DataTableHeaderProps;
    shippingDetailsTableHeaderStyle: DataTableHeadProps;
    tableCellStyle: DataTableCellProps;
    accordionCellStyle: DataTableCellProps;
    hiddenHeaderStyle: HiddenProps;
    tableCellGridStyle: GridItemProps;
    accordionRowStyle: DataTableRowProps;
    shippingDetailsStyle: LinkPresentationProps;
    showAccordionGridStyle: GridItemProps;
}

export const OrderDetailsTableStyle: OrderDetailsStyles = {
    tableWarpper: {
        css: css`
            @media print {
                page-break-inside: avoid;
            }
        `,
    },
    tableWrapperHeader: {
        css: css`
            display: flex;
            justify-content: space-between;
            align-items: center;
            flex-wrap: wrap;
        `,
    },
    infoLabel: {
        forwardAs: "div",
        size: 20,
        weight: 500,
        css: css`
            margin-bottom: 20px;
        `,
    },
    tableBodyStyle: {
        css: css`
            @media not print {
                ${screen("lte", "md")`
                    display: flex;
                    flex-direction: column;
                `}
            }

            @media print {
                tr {
                    flex-direction: row;
                    border: 1px solid ${color("common.ui4")};
                }
            }
        `,
    },
    tableBodyWrapper: {
        css: css`
        ${screen("lte", "md")`    
            ${onPrint(css`            
                & > thead {
                    display: block; 
                    width: 100%;
                }
            `)}
        `}
        
        `,
    },
    tableHeadStyle: {
        css: css`   
            ${screen("lte", "md")`
            display: inline-flex;
            width: 100%;
                @media not print {
                    ${TypographyStyle} {
                        display: none;
                    }
                }
                @media print {                    
                    &&& {                    
                        -webkit-print-color-adjust: exact;
                    }   
                }
            `}
        `,
    },
    tableHeaderStyle: {
        css: css`
            &:not(:last-child) {
                width: 15%;
            }
        `,
    },
    shippingDetailsTableHeaderStyle: {
        css: css`
            ${screen("lte", "md")`
                display: none;
            `}
        `,
    },
    tableCellStyle: {
        css: css`
            vertical-align: top;

            ${screen("lte", "md")`
                 display: flex;
                 align-items: center;
                &.hide-on-mobile {
                    display:none;
                }
            `}

        `,
    },
    accordionCellStyle: {
        css: css`
            cursor: auto;
            ${screen("lte", "md")`
                height: 100%;
            `}
        `,
    },
    hiddenHeaderStyle: {
        above: "md",
        css: css`
            min-width: 33%;
            font-weight: bold;
            ${printStyles.hideOnPrint}
            ${screen("lt", "sm")`
                min-width: 50%;
            `}
        `,
    },
    tableCellGridStyle: {
        width: [6, 6, 6, 12, 12],
        css: css`
            word-break: keep-all;
            white-space: break-spaces;
            font-size: 12px;
            &&& {
                max-width: 100%;
                padding: 0;
            }
            @media print {
                max-width: 100%;
                flex-basis: 100%;
            }
        `,
    },
    accordionRowStyle: {
        css: css`
            height: 40px;
            border-bottom: 1px solid ${getColor("common.ui4")};
            &&& {
                background-color: ${getColor("common.accent")};
            }
            ${screen("lte", "md")`
                display: flex;
                flex-direction: column;
                height: 100%;
            `}
        `,
    },
    shippingDetailsStyle: {
        color: "primary.main",
        typographyProps: {
            size: "14px",
        },
        css: css`
            border-color: ${getColor("primary.main")};
            &.hide-on-desktop {
                margin-bottom: 20px;
            }
            ${screen("gt", "md")`&.hide-on-desktop { display:none; }`}
            ${screen("lte", "md")`
                width: 100%;
                text-align: center;
                flex-direction: row-reverse;
                justify-content: center;
                padding: 3px;
                border-width: 1px;
                border-style: solid;
            `};
        `,
    },
    showAccordionGridStyle: {
        width: 12,
        css: css`
            justify-content: right;
            padding: 0;
            ${screen("lte", "md")`
                &.hide-on-mobile {
                    display:none;
                }
            `}
        `,
    },
};
